/**
 * PickingListDetail — Iter 290za.
 *
 * Standalone page for one picking list. Used when the user clicks a
 * list from /picking (hub) or from a job card's "Linked picking" block.
 *
 * Renders a slim header (back link + breadcrumb to project/customer if
 * available) then reuses the heavy ProjectPickingCard in `singleListId`
 * mode so we don't duplicate the items / movements / breakdown UI.
 *
 * Why this exists: before this page, clicking a picking list from the
 * Picking hub jumped to the parent /projects/{pid} page with a query
 * param — the picking section was technically rendered but the user
 * landed on the project overview header and couldn't see the items.
 * Worse, support-ticket-only pickings (no project_id) had a dangling
 * /projects/null?picking=... link that went nowhere.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, ClipboardList, Building2, FolderKanban, Printer, FileBarChart2, Lock, LockOpen, Truck, CheckCircle2, Ban, Pencil, PackageCheck } from "lucide-react";
import api from "../lib/api";
import { toast } from "../components/ui/sonner";
import ProjectPickingCard from "../components/projects/ProjectPickingCard";
import EntityLinksPanel from "../components/EntityLinksPanel";
import PickingSlipScanFlow from "../components/picking/PickingSlipScanFlow";
import { displayPickingListName } from "../lib/pickingListName";
import ProjectLinkChip from "../components/shared/ProjectLinkChip";
import DictTextarea from "../components/DictTextarea";

const STATUS_PILL = {
  draft:        { c: "#647087", l: "Draft" },
  open:         { c: "#1FB6C1", l: "Open" },
  in_progress:  { c: "#F59E0B", l: "In progress" },
  partial:      { c: "#F59E0B", l: "Partial" },
  picked:       { c: "#10B981", l: "Picked" },
  shipped:      { c: "#10B981", l: "Shipped" },
  closed:       { c: "#647087", l: "Closed" },
  voided:       { c: "#EF4444", l: "Voided" },
};

export default function PickingListDetail() {
  const { lid } = useParams();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [project, setProject] = useState(null);
  const [customer, setCustomer] = useState(null);
  // Iter 290zn — JC titles for the legacy-name fallback in heading +
  // breadcrumb. Without this, lists named "JC <short> materials" remain
  // unreadable until the storage migration runs.
  const [jcMap, setJcMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  // Iter 290zp — Picking-slip PDF export. State only tracks the click
  // → server round-trip; the PDF opens in a new tab once we have bytes.
  const [printing, setPrinting] = useState(false);
  // Iter 400.17 — Picking Report PDF export (with amounts + wastage totals).
  const [printingReport, setPrintingReport] = useState(false);
  // Iter 404 · Phase 4A — batch-fetch procurement status for this list's
  // picking_items so we can show a "stock inbound / stock arrived"
  // banner at the top of the picking detail. `null` = not-yet-loaded.
  const [procLookup, setProcLookup] = useState(null);

  // Iter 418.j (Dirk, 2026-02-28) — Inline-editable slip name. The
  // auto-generated "Picking · <SO number>" default is fine as a
  // starting point but ops need to be able to override it with an
  // easily-identifiable name (e.g. "Kruger Lodge — 2026 Q2 install").
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [savingName, setSavingName] = useState(false);

  // Iter 400.17 — shared PDF fetch → open helper used by both the
  // "Print slip" and "Report" buttons. `kind` picks the endpoint + toast.
  async function fetchAndOpenPdf(kind) {
    if (!list?.id) return;
    const isReport = kind === "report";
    const setBusy = isReport ? setPrintingReport : setPrinting;
    const busy = isReport ? printingReport : printing;
    if (busy) return;
    setBusy(true);
    try {
      const endpoint = isReport
        ? `/picking/lists/${list.id}/report-pdf`
        : `/picking/lists/${list.id}/pdf`;
      const resp = await api.get(endpoint, {
        responseType: "blob",
        timeout: 60_000,
      });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      let filename = isReport
        ? `PickingReport_${list.short_id || list.id.slice(0, 8)}.pdf`
        : `PickingSlip_${list.short_id || list.id.slice(0, 8)}.pdf`;
      const cd = resp.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="?([^";]+)"?/i);
      if (m) filename = m[1];
      const win = window.open(url, "_blank", "noopener,noreferrer");
      if (!win) {
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      const pn = resp.headers?.[
        isReport ? "x-picking-report-print-number" : "x-picking-print-number"
      ];
      if (pn) toast.success(isReport ? `Picking report generated (print #${pn}).` : `Picking slip printed (print #${pn}).`);
      else toast.success(isReport ? "Picking report generated." : "Picking slip generated.");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`picking ${kind} pdf export failed`, e);
      toast.error(
        isReport
          ? "Could not generate the picking report. Please retry."
          : "Could not generate the picking slip. Please retry.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function handleExportPdf() {
    return fetchAndOpenPdf("slip");
  }

  async function handleExportReportPdf() {
    return fetchAndOpenPdf("report");
  }

  // Iter 290zp.6 — pulled out into a callable so the OCR scan-apply
  // flow can re-fetch the list after writing through.
  const fetchList = async () => {
    setLoading(true);
    try {
      const { data: pl } = await api.get(`/picking/lists/${lid}`);
      setList(pl);
      if (pl.project_id) {
        api.get(`/projects/${pl.project_id}`).then((r) => setProject(r.data)).catch(() => {});
      }
      if (pl.customer_id) {
        api.get(`/customers/${pl.customer_id}`).then((r) => setCustomer(r.data)).catch(() => {});
      }
      const jids = pl.jobcard_ids || [];
      if (jids.length) {
        api.get(`/jobcards?ids=${jids.join(",")}&fields=id,title,short_id`).then((r) => {
          const m = {};
          for (const j of (r.data?.items || r.data || [])) m[j.id] = j;
          setJcMap(m);
        }).catch(() => {});
      }
      // Fetch procurement lookup for the picking_items on this list.
      // GET /picking/lists/{lid} already returns items — pull from `pl`.
      try {
        const picking_item_ids = (pl.items || [])
          .map((it) => it.id)
          .filter(Boolean);
        if (picking_item_ids.length) {
          const { data: pl2 } = await api.post(
            "/procurement/lookup/by-picking-items",
            { picking_item_ids },
          );
          setProcLookup(pl2?.lookup || {});
        } else {
          setProcLookup({});
        }
      } catch { setProcLookup({}); }
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
      else toast.error("Could not load picking list.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: pl } = await api.get(`/picking/lists/${lid}`);
        if (cancelled) return;
        setList(pl);
        // Best-effort hydrate breadcrumb
        if (pl.project_id) {
          api.get(`/projects/${pl.project_id}`).then((r) => !cancelled && setProject(r.data)).catch(() => {});
        }
        if (pl.customer_id) {
          api.get(`/customers/${pl.customer_id}`).then((r) => !cancelled && setCustomer(r.data)).catch(() => {});
        }
        // Iter 290zn — fetch JC titles for the legacy-name fallback.
        const jids = pl.jobcard_ids || [];
        if (jids.length) {
          Promise.all(jids.map((jid) => api.get(`/jobcards/${jid}`).then((r) => r.data).catch(() => null)))
            .then((results) => {
              if (cancelled) return;
              const m = {};
              results.filter(Boolean).forEach((j) => { m[j.id] = { id: j.id, title: j.title, short_id: j.short_id }; });
              setJcMap(m);
            });
        }
        // Iter 404 · Phase 4A — batch-fetch procurement lookup for
        // the list's picking_items so we can show a "stock inbound
        // / arrived" banner. Fire-and-forget; the banner renders
        // nothing when no picking_item has a linked procurement item.
        const pids = (pl.items || []).map((it) => it.id).filter(Boolean);
        if (pids.length) {
          api.post("/procurement/lookup/by-picking-items", { picking_item_ids: pids })
            .then((r) => !cancelled && setProcLookup(r.data?.lookup || {}))
            .catch(() => !cancelled && setProcLookup({}));
        } else {
          setProcLookup({});
        }
      } catch (e) {
        if (!cancelled) {
          if (e?.response?.status === 404) setNotFound(true);
          else toast.error("Could not load picking list.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [lid]);

  if (loading) {
    return (
      <div className="px-4 sm:px-6 py-12 text-center text-[#647087]">
        <Loader2 className="w-4 h-4 inline animate-spin" /> Loading picking list…
      </div>
    );
  }

  if (notFound || !list) {
    return (
      <div className="px-4 sm:px-6 py-12 text-center text-[#647087]" data-testid="picking-detail-notfound">
        <ClipboardList className="w-10 h-10 mx-auto mb-2 opacity-30" />
        <div className="text-sm">Picking list not found or you don't have access.</div>
        <button type="button" onClick={() => navigate("/picking")} className="hv-btn hv-btn-ghost text-xs mt-3">
          <ArrowLeft className="w-3 h-3" /> Back to Picking
        </button>
      </div>
    );
  }

  const pill = STATUS_PILL[list.status] || STATUS_PILL.draft;
  const t = list.totals || {};

  return (
    <div className="px-4 sm:px-6 py-5 max-w-5xl mx-auto" data-testid="picking-list-detail">
      {/* Header strip */}
      <div className="mb-4">
        <Link to="/picking" className="text-[11px] text-[#647087] hover:text-white inline-flex items-center gap-1 mb-2">
          <ArrowLeft className="w-3 h-3" /> All picking lists
        </Link>
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded shrink-0"
              style={{ background: `${pill.c}1f`, color: pill.c }}
              data-testid="picking-detail-status"
            >
              {pill.l}
            </span>
            {list.short_id && (
              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-white/10 text-[#a0aabf]">
                {list.short_id}
              </span>
            )}
            {!list.project_id && list.customer_id && (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-amber-400/30 text-amber-200 bg-amber-500/10" title="No project — support / loose picking">
                Loose (no project)
              </span>
            )}
            {/* [Iter 420] Reverse project-link chip. */}
            <ProjectLinkChip
              kind="picking_list"
              entityId={list.id}
              projectId={list.project_id}
              customerId={list.customer_id}
              projectShortId={project?.short_id}
              projectTitle={project?.title}
              onLinked={async () => {
                try {
                  const { data } = await api.get(`/picking/lists/${list.id}`);
                  setList(data);
                  if (data.project_id) {
                    const pr = await api.get(`/projects/${data.project_id}`);
                    setProject(pr.data);
                  }
                } catch {}
              }}
              onUnlinked={async () => {
                try {
                  const { data } = await api.get(`/picking/lists/${list.id}`);
                  setList(data);
                  setProject(null);
                } catch {}
              }}
            />
          </div>
          {/* Iter 418.j — inline-editable slip name. Owner/manager/rep
              can rename to a friendlier label. Save with Enter or the
              tick button; cancel with Esc. */}
          {editingName ? (
            <div className="flex items-center gap-2 flex-wrap">
              <input
                type="text"
                value={nameDraft}
                autoFocus
                onChange={(e) => setNameDraft(e.target.value)}
                onKeyDown={async (e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    setSavingName(true);
                    try {
                      const { data } = await api.patch(`/picking/lists/${list.id}`, { name: nameDraft.trim() || null });
                      setList((prev) => ({ ...(prev || {}), ...data }));
                      setEditingName(false);
                      toast.success("Picking slip renamed.");
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "Rename failed.");
                    } finally { setSavingName(false); }
                  } else if (e.key === "Escape") {
                    setEditingName(false);
                  }
                }}
                className="hv-input font-display text-xl md:text-2xl font-bold w-full max-w-2xl"
                placeholder="Picking slip name…"
                data-testid="picking-detail-name-input"
              />
              <button
                type="button"
                disabled={savingName}
                onClick={async () => {
                  setSavingName(true);
                  try {
                    const { data } = await api.patch(`/picking/lists/${list.id}`, { name: nameDraft.trim() || null });
                    setList((prev) => ({ ...(prev || {}), ...data }));
                    setEditingName(false);
                    toast.success("Picking slip renamed.");
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Rename failed.");
                  } finally { setSavingName(false); }
                }}
                className="hv-btn hv-btn-primary text-xs"
                data-testid="picking-detail-name-save"
              >
                {savingName ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
              </button>
              <button
                type="button"
                onClick={() => setEditingName(false)}
                className="hv-btn hv-btn-ghost text-xs"
                data-testid="picking-detail-name-cancel"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 flex-wrap group">
              <h1
                className="font-display text-2xl md:text-3xl font-bold tracking-tight break-words"
                data-testid="picking-detail-name"
              >
                {displayPickingListName(list, jcMap) || "Picking list"}
              </h1>
              <button
                type="button"
                onClick={() => {
                  setNameDraft(displayPickingListName(list, jcMap) || "");
                  setEditingName(true);
                }}
                className="opacity-40 group-hover:opacity-100 hover:opacity-100 transition text-[#647087] hover:text-white p-1 rounded"
                title="Rename this picking slip"
                data-testid="picking-detail-name-edit-btn"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          <div className="text-[11px] text-[#647087] flex items-center gap-2 flex-wrap">
            {project ? (
              <Link to={`/projects/${project.id}`} className="inline-flex items-center gap-1 hover:text-white">
                <FolderKanban className="w-3 h-3" /> {project.short_id || project.title || "Project"}
              </Link>
            ) : list.project_id ? (
              <span className="inline-flex items-center gap-1">
                <FolderKanban className="w-3 h-3" /> Project {String(list.project_id).slice(0, 8)}
              </span>
            ) : null}
            {customer && (
              <Link to={`/customers/${customer.id}`} className="inline-flex items-center gap-1 hover:text-white">
                <Building2 className="w-3 h-3" /> {customer.name}
              </Link>
            )}
            {list.due_date && <span>· due {list.due_date}</span>}
            {list.created_by_name && <span>· by {list.created_by_name}</span>}
          </div>

          {/* Iter 411 — Action row on its own line so the title above
              can flow naturally instead of getting crushed into a
              narrow flex column (Dirk's "Picking · SO SAL2888.11" was
              wrapping to 3 lines while wide-open whitespace sat on
              the right of the screen). */}
          <div className="flex items-center gap-2 flex-wrap">
            <PickingSlipScanFlow
              listId={list.id}
              isSignedOff={!!list?.signed_off_at}
              onApplied={() => { fetchList?.(); }}
            />
            <button
              type="button"
              onClick={handleExportPdf}
              disabled={printing}
              data-testid="picking-detail-export-pdf"
              title="Export a printable picking slip for pen-and-paper backup"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded border border-white/10 bg-white/5 text-[11px] text-white hover:bg-white/10 disabled:opacity-50 disabled:cursor-wait transition"
            >
              {printing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Printer className="w-3.5 h-3.5" />}
              <span>{printing ? "Generating…" : "Print slip"}</span>
            </button>
            <button
              type="button"
              onClick={handleExportReportPdf}
              disabled={printingReport}
              data-testid="picking-detail-export-report-pdf"
              title="Generate a financial report — same picking rows plus unit prices and total values (wastage per job)"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded border border-emerald-400/40 bg-emerald-500/10 text-[11px] text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-50 disabled:cursor-wait transition"
            >
              {printingReport ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileBarChart2 className="w-3.5 h-3.5" />}
              <span>{printingReport ? "Generating…" : "Report"}</span>
            </button>
            <ReadyToPickButton
              list={list}
              onChanged={fetchList}
            />
            <CompleteReopenButton
              list={list}
              onChanged={fetchList}
            />
            <CloseReopenButton
              list={list}
              customer={customer}
              onChanged={fetchList}
            />
            {list?.status !== "voided" && (
              <VoidListButton list={list} onChanged={fetchList} />
            )}
          </div>

          {/* Totals — full-width bar so the numbers get room to breathe. */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
            <Totals label="Rows"        v={t.rows ?? 0} accent="#a0aabf" />
            <Totals label="Required"    v={fmt(t.qty_required)} accent="#0EA5E9" />
            <Totals label="Picked"      v={fmt(t.qty_picked)} accent="#10B981" />
            <Totals label="Outstanding" v={fmt(t.qty_outstanding)} accent={t.qty_outstanding > 0 ? "#F59E0B" : "#647087"} />
          </div>
        </div>
      </div>

      {/* Iter 411 — Voided banner. Loud, prominent, immediate so
          nobody accidentally starts picking off a cancelled slip. */}
      {list?.status === "voided" && (
        <div
          className="rounded-md border-2 border-rose-500/60 bg-rose-500/10 p-3 flex items-start gap-3"
          data-testid="picking-detail-voided-banner"
        >
          <Ban className="w-5 h-5 text-rose-300 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-rose-200">
              This picking slip has been voided.
            </div>
            {list.void_reason && (
              <div className="text-[11px] text-rose-100/80 mt-1">
                <span className="text-rose-300/70 uppercase tracking-wider text-[9px] font-mono mr-1">Reason:</span>
                {list.void_reason}
              </div>
            )}
            <div className="text-[10px] text-rose-200/60 mt-1 font-mono">
              {list.voided_at && `Voided ${list.voided_at.slice(0, 16).replace("T", " ")}`}
              {list.voided_by_name && ` · by ${list.voided_by_name}`}
            </div>
          </div>
        </div>
      )}

      {/* List-level Notes — read/write the same `notes` blob that's
          printed on the picking slip PDF (last page). Persists via
          PATCH /api/picking/lists/{lid}. */}
      <ListNotesAndSignoff
        list={list}
        onSaved={(patch) => setList((prev) => ({ ...prev, ...patch }))}
      />

      {/* Iter 404 · Phase 4A — Procurement status banner.
          Aggregates inbound (still outstanding) vs arrived (all
          delivered) picking rows so techs can see at a glance
          whether stock is en-route or already received. */}
      <ProcurementBanner lookup={procLookup} items={list.items || []} />

      {/* Iter 411 — Picking rows are the primary content of this page.
          They render BEFORE the linked-docs panel so the operator
          sees the actual work first. Sales orders / job cards this
          slip is linked to move to the bottom (context, not action). */}
      <ProjectPickingCard
        projectId={list.project_id || ""}
        singleListId={list.id}
        onListChanged={fetchList}
      />

      {/* Cross-entity links — Iter 290zb. Many-to-many across JC ↔ SO.
          Iter 395 — showPullActions=true so linking an SO also pulls
          its line items in one click (idempotent), and each linked SO
          row gets a "Pull items" button so techs can add another SO
          to an existing picking slip. Iter 411 — moved BELOW the
          picking rows so it acts as "linked context" rather than
          hijacking the top of the page. */}
      <EntityLinksPanel
        entityType="picking"
        entityId={list.id}
        linkTo={["sales_doc", "jobcard"]}
        className="mt-4"
        showPullActions
      />
    </div>
  );
}

function Totals({ label, v, accent }) {
  return (
    <div className="px-2 py-1.5 rounded border border-white/5 bg-white/[0.02] min-w-[60px]">
      <div className="text-[9px] uppercase tracking-wider text-[#647087]">{label}</div>
      <div className="text-sm font-mono font-bold mt-0.5" style={{ color: accent }}>{v}</div>
    </div>
  );
}

function fmt(n) {
  if (n === undefined || n === null) return "0";
  const num = Number(n);
  if (Number.isInteger(num)) return String(num);
  return num.toFixed(2).replace(/\.?0+$/, "");
}

/**
 * Iter 290zp.5 — Picking-list Notes + Sign-off summary.
 *
 * The printed picking slip's last page renders the list's `notes` blob
 * + `signed_off_by_name` / `signed_off_at`. Per the "PDF reads from the
 * app" rule, every printed field needs a home in the in-app UI to
 * read/write the same data. This component is that home.
 *
 * Notes is a single textarea per picking list (matches "one overall
 * picking slip note" from the brief). Sign-off is read-only here —
 * the actual sign-off action lives in the ProjectPickingCard
 * complete/sign-off flow.
 */
function ListNotesAndSignoff({ list, onSaved }) {
  const [draft, setDraft] = useState(list?.notes || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setDraft(list?.notes || ""); }, [list?.notes]);
  const dirty = (draft || "") !== (list?.notes || "");
  const onSave = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      await api.patch(`/picking/lists/${list.id}`, { notes: draft });
      onSaved?.({ notes: draft });
      toast.success("Picking list notes saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save notes.");
    } finally {
      setSaving(false);
    }
  };
  const signedName = list?.signed_off_by_name;
  const signedAt = list?.signed_off_at;
  return (
    <div className="mb-4 rounded border border-white/5 bg-white/[0.02] p-3 sm:p-4" data-testid="picking-detail-notes-block">
      <div className="flex items-start gap-2 mb-2">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-[#94a3b8] shrink-0 mt-1">Notes</div>
        <div className="flex-1 text-[10px] text-[#647087] mt-1">
          Printed on the picking slip&apos;s last page. One overall note per picking list.
        </div>
        {dirty && (
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            data-testid="picking-detail-notes-save"
            className="text-[11px] px-2.5 py-1 rounded border border-emerald-400/40 bg-emerald-500/[0.12] text-emerald-200 hover:bg-emerald-500/[0.22] disabled:opacity-50 shrink-0"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        )}
      </div>
      <DictTextarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={onSave}
        rows={3}
        placeholder="E.g. site notes, customer instructions, anything that should accompany the printed slip…"
        className="hv-input w-full text-[12px] py-2"
        data-testid="picking-detail-notes-textarea"
      />
      {(signedName || signedAt) && (
        <div className="mt-3 pt-3 border-t border-white/5 flex items-center gap-3 text-[11px] flex-wrap">
          <span className="text-[10px] uppercase tracking-wider text-[#647087]">Signed off</span>
          {signedName && <span className="text-emerald-200 font-medium">{signedName}</span>}
          {signedAt && <span className="text-[#a0aabf] font-mono text-[10px]">{new Date(signedAt).toLocaleString()}</span>}
        </div>
      )}
    </div>
  );
}



/**
 * VoidListButton — Iter 411 (Dirk, 2026-02-28).
 *
 * Cancel a picking slip when the source sales order was wrong (e.g.
 * OCR / pricing error requiring a fresh SO). Two-step interaction to
 * prevent accidental voids:
 *   1. Click "Void" → opens an inline confirm panel.
 *   2. Operator types the reason (required) and taps "Confirm void".
 *
 * Server refuses if any row is dispatched (returning that stock first
 * preserves the movement audit chain). On success, the source SO is
 * unlinked so a new picking slip can be generated from the corrected
 * SO without an idempotency collision.
 */
function VoidListButton({ list, onChanged }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const doVoid = async () => {
    const r = reason.trim();
    if (r.length < 3) {
      toast.error("Please enter a reason (at least 3 characters).");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/picking/lists/${list.id}/void`, {
        reason: r,
        unlink_source_docs: true,
      });
      const unlinked = (data?.unlinked_source_docs || []).length;
      toast.success(
        unlinked > 0
          ? `Picking slip voided. ${unlinked} source sales order${unlinked === 1 ? "" : "s"} unlinked — you can now generate a fresh picking slip.`
          : "Picking slip voided.",
      );
      setOpen(false);
      setReason("");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not void this list.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded border text-[11px] border-rose-400/40 bg-rose-500/10 text-rose-200 hover:bg-rose-500/20 transition"
        data-testid="picking-detail-void-open"
        title="Cancel this picking slip — source SO will be unlinked so you can generate a fresh one."
      >
        <Ban className="w-3.5 h-3.5" />
        <span>Void</span>
      </button>
    );
  }

  return (
    <div
      className="rounded border border-rose-400/40 bg-rose-500/[0.06] p-2 space-y-2 min-w-[280px]"
      data-testid="picking-detail-void-panel"
    >
      <div className="text-[10px] uppercase tracking-wider text-rose-300 font-bold flex items-center gap-1">
        <Ban className="w-3 h-3" /> Void this picking slip?
      </div>
      <div className="text-[10px] text-[#a0aabf] leading-snug">
        The source sales order will be unlinked so you can upload a corrected SO and generate a fresh picking slip. The voided list stays in the archive for audit reference.
      </div>
      <DictTextarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Reason (required) — e.g. 'SO had wrong SKU on line 12, replacing with SAL2888.11'"
        rows={2}
        className="w-full bg-black/40 border border-white/10 rounded px-2 py-1 text-[11px] text-white placeholder:text-[#647087] focus:border-rose-400/60 focus:outline-none resize-none"
        data-testid="picking-detail-void-reason"
        autoFocus
      />
      <div className="flex items-center justify-end gap-1.5">
        <button
          type="button"
          onClick={() => { setOpen(false); setReason(""); }}
          disabled={busy}
          className="hv-btn hv-btn-ghost text-[11px] disabled:opacity-50"
          data-testid="picking-detail-void-cancel"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={doVoid}
          disabled={busy || reason.trim().length < 3}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded text-[11px] bg-rose-500 hover:bg-rose-400 text-white disabled:opacity-50 transition"
          data-testid="picking-detail-void-confirm"
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Ban className="w-3 h-3" />}
          Confirm void
        </button>
      </div>
    </div>
  );
}


// Iter 418.bn (Dirk, 2026-02-28) — Mark Ready to Pick. Sits between
// Report and Mark Complete on the header toolbar. Sets `ready_at`
// which flips the derived phase from Open → Ready. Once the picker
// logs the first pick, the phase auto-progresses to Picking (row
// totals take over).
function ReadyToPickButton({ list, onChanged }) {
  const [busy, setBusy] = useState(false);
  const isReady = !!list?.ready_at;
  const isComplete = !!list?.completed_at;
  const isClosed = !!list?.closed_at;
  // Hide once any picks have started (phase >= picking) — button is
  // only meaningful in the Open / Ready transition.
  const totals = list?.totals || {};
  const anyPicked = (totals.qty_picked || 0) > 0;
  if (isClosed || isComplete || list?.status === "voided" || anyPicked) return null;
  const label = isReady ? "Un-mark ready" : "Mark as Ready to Pick";
  const orange = "border-orange-400/40 bg-orange-500/10 text-orange-200 hover:bg-orange-500/20";
  const grey = "border-white/10 bg-white/5 text-[#a0aabf] hover:bg-white/10";
  const run = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const url = isReady
        ? `/picking/lists/${list.id}/unmark-ready`
        : `/picking/lists/${list.id}/mark-ready`;
      await api.post(url, {});
      toast.success(isReady ? "Slip returned to Open." : "Slip marked Ready to Pick.");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update status.");
    } finally { setBusy(false); }
  };
  return (
    <button
      type="button"
      onClick={run}
      disabled={busy}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded border text-[11px] disabled:opacity-50 transition ${isReady ? grey : orange}`}
      data-testid="picking-detail-ready-btn"
      title={isReady
        ? "Un-mark this slip as ready — drops back to Open"
        : "Mark this slip as Ready to Pick — hand off to the picker"}
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PackageCheck className="w-3.5 h-3.5" />}
      <span>{label}</span>
    </button>
  );
}

// Iter 418.k (Dirk, 2026-02-28) — Mark Complete button for the
// picking list header. "Complete" means the slip has been through the
// full pick→dispatch→return cycle and is awaiting invoicing. Sits
// BETWEEN Returned and Closed. Reopen (Un-mark) drops back to the
// derived phase (returned / dispatched / picking).
function CompleteReopenButton({ list, onChanged }) {
  const [busy, setBusy] = useState(false);
  const isComplete = !!list?.completed_at;
  const isClosed = !!list?.closed_at;
  if (isClosed || list?.status === "voided") return null;
  const label = isComplete ? "Un-mark complete" : "Mark complete";
  const emerald = "border-emerald-400/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20";
  const grey = "border-white/10 bg-white/5 text-[#a0aabf] hover:bg-white/10";
  const run = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const url = isComplete
        ? `/picking/lists/${list.id}/uncomplete`
        : `/picking/lists/${list.id}/complete`;
      await api.post(url, {});
      toast.success(isComplete ? "Picking slip re-opened for changes." : "Picking slip marked complete.");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update status.");
    } finally { setBusy(false); }
  };
  return (
    <button
      type="button"
      onClick={run}
      disabled={busy}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded border text-[11px] disabled:opacity-50 transition ${isComplete ? grey : emerald}`}
      data-testid="picking-detail-complete-btn"
      title={isComplete
        ? "Un-mark this slip as complete — falls back to the derived phase (returned/dispatched/picking) based on row totals"
        : "Mark this slip as complete — signals it's been through the full pick→dispatch→return cycle and is awaiting invoicing"}
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
      <span>{label}</span>
    </button>
  );
}

// Iter 398f — Close / Reopen control for the picking list header.
// When the list has an invoice on the customer AND is still open, the
// button pulses amber as a gentle nudge (Dirk explicitly asked for a
// prompt, not auto-close, so the operator retains the trigger).
function CloseReopenButton({ list, customer, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [hasInvoice, setHasInvoice] = useState(false);

  useEffect(() => {
    if (!customer?.id || list?.closed_at) { setHasInvoice(false); return; }
    let alive = true;
    (async () => {
      try {
        const r = await api.get(
          `/sales-documents?customer_id=${customer.id}&category=invoice`,
        );
        if (!alive) return;
        setHasInvoice(Array.isArray(r.data) && r.data.length > 0);
      } catch { /* silent — nudge is optional */ }
    })();
    return () => { alive = false; };
  }, [customer?.id, list?.closed_at]);

  const isClosed = !!list?.closed_at;
  const promptClose = !isClosed && hasInvoice;

  const doClose = async () => {
    if (!window.confirm("Close this picking list? It stays visible in the Closed bucket for history.")) return;
    setBusy(true);
    try {
      await api.post(`/picking/lists/${list.id}/close`);
      toast.success("List closed.");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not close list.");
    } finally { setBusy(false); }
  };
  const doReopen = async () => {
    setBusy(true);
    try {
      await api.post(`/picking/lists/${list.id}/reopen`);
      toast.success("List reopened.");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reopen list.");
    } finally { setBusy(false); }
  };

  return (
    <button
      type="button"
      onClick={isClosed ? doReopen : doClose}
      disabled={busy}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded border text-[11px] transition ${
        isClosed
          ? "border-white/10 bg-white/5 text-white hover:bg-white/10"
          : promptClose
          ? "border-amber-400/50 bg-amber-500/15 text-amber-200 hover:bg-amber-500/25 animate-pulse"
          : "border-white/10 bg-white/5 text-white hover:bg-white/10"
      } disabled:opacity-50`}
      data-testid={isClosed ? "picking-detail-reopen" : "picking-detail-close"}
      title={
        isClosed
          ? "Reopen this list"
          : promptClose
          ? "This customer has invoices — close if the job is done"
          : "Close this list once the invoice is done"
      }
    >
      {busy
        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
        : isClosed
        ? <LockOpen className="w-3.5 h-3.5" />
        : <Lock className="w-3.5 h-3.5" />}
      <span>{isClosed ? "Reopen" : (promptClose ? "Close list?" : "Close list")}</span>
    </button>
  );
}


// Iter 404 · Phase 4A · ProcurementBanner
// Aggregates the per-picking_item procurement lookup response into two
// summary counts + a collapsible detail list. Renders nothing when no
// picking_item on this list has any procurement link — this keeps the
// picking detail page unchanged for lists that don't use procurement.
function ProcurementBanner({ lookup, items }) {
  const [open, setOpen] = useState(false);
  if (!lookup || Object.keys(lookup).length === 0) return null;

  const itemById = Object.fromEntries((items || []).map((i) => [i.id, i]));
  const rows = Object.entries(lookup).map(([pid, agg]) => ({
    picking_item_id: pid,
    picking_item: itemById[pid],
    ...agg,
  }));
  const inbound = rows.filter((r) => Number(r.qty_outstanding || 0) > 0);
  const arrived = rows.filter(
    (r) =>
      Number(r.qty_outstanding || 0) <= 0 && Number(r.qty_delivered || 0) > 0,
  );
  if (inbound.length === 0 && arrived.length === 0) return null;

  const tone = inbound.length > 0
    ? { border: "border-cyan-500/40", chip: "bg-cyan-500/15 border-cyan-500/40 text-cyan-200", icon: "text-cyan-300" }
    : { border: "border-emerald-500/40", chip: "bg-emerald-500/15 border-emerald-500/40 text-emerald-200", icon: "text-emerald-300" };

  return (
    <div className={`hv-card p-3 mb-4 ${tone.border}`}
         data-testid="picking-procurement-banner">
      <button onClick={() => setOpen((v) => !v)}
              className="w-full flex items-center gap-2 text-left"
              data-testid="picking-procurement-banner-toggle">
        <div className={`p-1.5 rounded border ${tone.chip}`}>
          {inbound.length > 0
            ? <Truck className={`w-3.5 h-3.5 ${tone.icon}`} />
            : <CheckCircle2 className={`w-3.5 h-3.5 ${tone.icon}`} />}
        </div>
        <div className="flex-1">
          <div className="text-sm font-bold text-white">
            {inbound.length > 0 ? (
              <>
                {inbound.length} row{inbound.length === 1 ? "" : "s"} inbound
                {arrived.length > 0 && ` · ${arrived.length} arrived`}
              </>
            ) : (
              <>Stock arrived on {arrived.length} row{arrived.length === 1 ? "" : "s"} — pick now</>
            )}
          </div>
          <div className="text-[10px] text-[#a0aabf]">
            Linked to procurement · click to {open ? "collapse" : "expand"}
          </div>
        </div>
        {inbound.length > 0 && inbound[0].earliest_eta && (
          <span className="text-[10px] text-cyan-300/80 shrink-0">
            next ETA {inbound[0].earliest_eta}
          </span>
        )}
      </button>
      {open && (
        <div className="mt-2 space-y-1"
             data-testid="picking-procurement-banner-detail">
          {rows.map((r) => {
            const isDone = Number(r.qty_outstanding || 0) <= 0
              && Number(r.qty_delivered || 0) > 0;
            const name = r.picking_item?.name || r.picking_item?.sku
              || r.lines?.[0]?.name || "—";
            return (
              <div key={r.picking_item_id}
                   className="p-1.5 rounded bg-black/20 border border-white/5 flex items-center gap-2 text-xs"
                   data-testid={`picking-procurement-row-${r.picking_item_id}`}>
                {isDone
                  ? <CheckCircle2 className="w-3 h-3 text-emerald-400 shrink-0" />
                  : <Truck className="w-3 h-3 text-cyan-300 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-[#dde3ec] truncate">{name}</div>
                  <div className="text-[10px] text-[#a0aabf]">
                    ordered {r.qty_ordered} · delivered {r.qty_delivered}
                    {!isDone && r.earliest_eta && ` · ETA ${r.earliest_eta}`}
                    {isDone && r.latest_grv_date && ` · received ${r.latest_grv_date}`}
                    {r.tracker_ids?.length > 0 && (
                      <>
                        {" · "}
                        {r.tracker_ids.map((tid, idx) => (
                          <Link key={tid} to={`/procurement/${tid}`}
                                className="text-cyan-300 hover:underline">
                            PO#{tid.slice(3, 11)}{idx < r.tracker_ids.length - 1 ? ", " : ""}
                          </Link>
                        ))}
                      </>
                    )}
                  </div>
                </div>
                <span className={`text-[10px] px-1.5 py-0.5 rounded border shrink-0 ${
                  isDone
                    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                    : "border-cyan-500/40 bg-cyan-500/10 text-cyan-300"
                }`}>
                  {isDone ? "arrived" : `${r.qty_outstanding} out`}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
